import { type FC, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import {
  MarketingContactSourceType,
  type ContactRowInputType,
} from "@/types/graphql/partner.generated";
import {
  marketingErrorKey,
  useImportMarketingContactsMutation,
} from "@/partner/entities/marketing";
import { FileUploadZone } from "@/partner/shared/ui";
import { parseCsvInput, parseTextInput } from "../model/parseContactsInput";
import {
  IMPORT_LIMITS,
  runChunks,
  splitChunks,
  type ChunksRun,
} from "../model/importChunks";
import { ImportResultAlert } from "./ImportResultAlert";

type ImportMode = "text" | "csv";

interface CsvFile {
  name: string;
  rows: ContactRowInputType[];
}

interface ImportContactsDialogProps {
  listId: string;
  onClose: () => void;
}

/**
 * Импорт адресов в список: вставка текстом или CSV. Строки уходят пачками
 * по IMPORT_LIMITS.chunk строго по очереди, ошибка пачки останавливает
 * импорт, загруженное остаётся. Списки и таблица перечитываются один раз
 * в конце, а не после каждой пачки.
 */
export const ImportContactsDialog: FC<ImportContactsDialogProps> = ({
  listId,
  onClose,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [mode, setMode] = useState<ImportMode>("text");
  const [text, setText] = useState("");
  const [csv, setCsv] = useState<CsvFile | null>(null);
  const [consent, setConsent] = useState(false);
  const [running, setRunning] = useState(false);
  const [doneChunks, setDoneChunks] = useState(0);
  const [run, setRun] = useState<ChunksRun | null>(null);
  const [importContacts] = useImportMarketingContactsMutation({
    refetchQueries: [],
  });

  const textRows = useMemo(() => parseTextInput(text), [text]);
  const rows = mode === "text" ? textRows : (csv?.rows ?? []);
  const tooMany = rows.length > IMPORT_LIMITS.max;
  const canImport = rows.length > 0 && consent && !tooMany && !running;
  const finished = run !== null;
  const doneRows = Math.min(doneChunks * IMPORT_LIMITS.chunk, rows.length);

  const onFileSelect = async (file: File) => {
    setCsv({ name: file.name, rows: parseCsvInput(await file.text()) });
  };

  const onImport = async () => {
    if (!canImport) return;
    const chunks = splitChunks(rows, IMPORT_LIMITS.chunk);
    const source =
      mode === "csv"
        ? MarketingContactSourceType.CsvType
        : MarketingContactSourceType.ManualType;
    setRunning(true);
    setDoneChunks(0);
    const result = await runChunks(
      chunks,
      async (chunk) => {
        const response = await importContacts({
          variables: {
            input: { listId, rows: chunk, source, consentConfirmed: true },
          },
        });
        const totals = response.data?.importMarketingContacts;
        if (!totals) throw new Error("CONTACT_IMPORT_FAILED");
        return {
          added: totals.added,
          updated: totals.updated,
          invalid: totals.invalid,
          duplicates: totals.duplicates,
          matchedViewers: totals.matchedViewers,
        };
      },
      setDoneChunks
    );
    setRun(result);
    setRunning(false);
    await client.refetchQueries({
      include: ["ContactLists", "ContactList", "MarketingContacts"],
    });
  };

  // Ошибка Apollo несёт код отказа; прочее показываем общим текстом.
  const runError = run?.error instanceof Error ? run.error : null;

  return (
    <Dialog
      open
      onClose={running ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={onClose}
            disabled={running}
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {t("marketingContacts.import.title")}
          </Typography>
          {finished ? (
            <Button variant="contained" disableElevation onClick={onClose}>
              {t("marketingContacts.import.done")}
            </Button>
          ) : (
            <Button
              variant="contained"
              disableElevation
              onClick={onImport}
              disabled={!canImport}
            >
              {t("marketingContacts.import.action")}
            </Button>
          )}
        </Toolbar>
        <Divider />
      </AppBar>
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            disabled={running || finished}
            onChange={(_event, next: ImportMode | null) => {
              if (next) setMode(next);
            }}
          >
            <ToggleButton value="text">
              {t("marketingContacts.import.modeText")}
            </ToggleButton>
            <ToggleButton value="csv">
              {t("marketingContacts.import.modeCsv")}
            </ToggleButton>
          </ToggleButtonGroup>
          {mode === "text" ? (
            <TextField
              value={text}
              onChange={(event) => setText(event.target.value)}
              multiline
              minRows={8}
              fullWidth
              disabled={running || finished}
              helperText={t("marketingContacts.import.textHint")}
            />
          ) : (
            <Box>
              <FileUploadZone
                accept=".csv,text/csv"
                maxSize={IMPORT_LIMITS.fileBytes}
                disabled={running || finished}
                onFileSelect={(file) => void onFileSelect(file)}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                {t("marketingContacts.import.csvHint")}
              </Typography>
              {csv && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {csv.name}
                </Typography>
              )}
            </Box>
          )}
          {(mode === "text" ? text.trim() !== "" : csv !== null) && (
            <Typography variant="body2" color="text.secondary">
              {t("marketingContacts.import.found", { count: rows.length })}
            </Typography>
          )}
          {tooMany && (
            <Alert severity="error">
              {t("marketingContacts.import.tooMany", {
                max: IMPORT_LIMITS.max,
              })}
            </Alert>
          )}
          <FormControlLabel
            disabled={running || finished}
            control={
              <Checkbox
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
              />
            }
            label={t("marketingContacts.import.consent")}
          />
          {running && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={rows.length > 0 ? (doneRows / rows.length) * 100 : 0}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {t("marketingContacts.import.progress", {
                  done: doneRows,
                  total: rows.length,
                })}
              </Typography>
            </Box>
          )}
          {run && run.error !== null && (
            <Alert severity="error">
              <Typography variant="body2">
                {t("marketingContacts.import.partial", {
                  done: run.done,
                  total: run.total,
                })}
              </Typography>
              <Typography variant="body2">
                {t(marketingErrorKey(runError))}
              </Typography>
            </Alert>
          )}
          {run && (run.error === null || run.done > 0) && (
            <ImportResultAlert totals={run.totals} />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
